import type React from 'react'
import styled, { css } from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import type { TypographyProps } from '@components/ui/Typography'
import { Typography } from '@components/ui/Typography'

export const LIST_ITEM_CSS_VARIABLES = {
  listItemHorizontalPadding: '--list-item-horizontal-padding',
  listItemTextColor: '--list-item-text-color',
  listItemVerticalPadding: '--list-item-vertical-padding',
  menuItemHoverColor: '--list-item-hover-color',
  menuItemSelectedColor: '--list-item-selected-color',
}

const StyledList = styled.ul<{
  $dense: boolean
  $disableGutters: boolean
  $disablePadding: boolean
  $fullWidth: boolean
}>`
  ${({ $disablePadding, $fullWidth }) => css`
    margin: 0;
    padding: 0;
    position: relative;
    list-style: none;

    ${!$disablePadding &&
    css`
      padding-top: 8px;
      padding-bottom: 8px;
    `}

    ${$fullWidth &&
    css`
      width: 100%;
    `}

    li {
      ${listItemStyles}
    }
  `}
`

function getCssVariables(dense: boolean, disableGutters: boolean): React.CSSProperties {
  return {
    ...(dense && { [LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding]: '4px' }),
    ...(disableGutters && {
      [LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding]: '0',
    }),
  }
}

interface ListProps extends React.HTMLAttributes<HTMLUListElement | HTMLOListElement | HTMLDivElement> {
  component?: 'ul' | 'ol' | 'nav'
  dense?: boolean
  disableGutters?: boolean
  disablePadding?: boolean
  fullWidth?: boolean
  menu?: boolean
}

export function List({
  component = 'ul',
  dense = false,
  disablePadding = false,
  disableGutters = false,
  fullWidth = false,
  menu = false,
  ...rest
}: ListProps) {
  return (
    <StyledList
      as={component}
      $dense={dense}
      $disableGutters={disableGutters}
      $disablePadding={disablePadding}
      $fullWidth={fullWidth}
      {...rest}
      style={{ ...rest.style, ...getCssVariables(dense, disableGutters) }}
      {...(menu && { role: 'menu', tabIndex: -1 })}
    />
  )
}

const listItemStyles = css<{ $dense: boolean; $disableGutters: boolean }>`
  ${({ theme, $dense, $disableGutters }) => css`
    ${!$disableGutters &&
    css`
      padding-left: var(${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}, 16px);
      padding-right: var(${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}, 16px);
    `}

    --horizontal-padding: var(${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}, ${$disableGutters ? '0' : '16px'});
    --horizontal-offset: calc(var(--horizontal-padding) * -1);
    --vertical-padding: var(${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}, ${$dense ? '4px' : '8px'});
    --vertical-offset: calc(var(--vertical-padding) * -1);

    padding-top: var(--vertical-padding);
    padding-bottom: var(--vertical-padding);
    width: 100%;
    color: var(${LIST_ITEM_CSS_VARIABLES.listItemTextColor}, inherit);

    &,
    ${ListItemButton} {
      display: flex;
      position: relative;
      text-align: left;
      align-items: center;
      justify-content: flex-start;
      text-decoration: none;
      font-size: ${theme.typography.inherit.fontSize};
      line-height: ${theme.typography.inherit.lineHeight};

      ${StyledListItemText} > *:first-child {
        --primary-text-align: ${$dense ? 'center' : 'left'};
      }
    }

    ${ListItemButton} {
      /* Due to calc usage, shorthand can not be used */
      margin-top: var(--vertical-offset);
      margin-bottom: var(--vertical-offset);
      margin-left: var(--horizontal-offset);
      margin-right: var(--horizontal-offset);
      padding: var(--vertical-padding) var(--horizontal-padding);
    }
  `}
`

const StyledListItem = styled.li<{
  $dense: boolean
  $disableGutters: boolean
  $divider: boolean
}>`
  ${listItemStyles}

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
  }

  &[role='menuitem'] {
    cursor: pointer;
  }

  &[role='menuitem']:not([data-selected='true']):hover {
    text-decoration: none;
    background-color: var(
      ${LIST_ITEM_CSS_VARIABLES.menuItemHoverColor},
      ${({ theme }) => applyOpacity(theme.colors.common.black, 4)}
    );
  }

  &[data-selected='true'] {
    background-color: var(${LIST_ITEM_CSS_VARIABLES.menuItemSelectedColor}, ${({ theme }) => theme.colors.blue[40]});
    color: ${({ theme }) => theme.colors.gray[0]};
  }

  ${({ $divider, theme }) => css`
    ${$divider &&
    css`
      border-bottom: 1px solid ${applyOpacity(theme.colors.common.black, 12)};
      background-clip: padding-box;
    `}
  `}
`

const ListItemButton = styled.button`
  margin: 0;
  padding: 0;
  width: calc(100% + 2 * var(--horizontal-padding, 0));
  background: none;
  border: none;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  cursor: pointer;

  ${({ theme }) => css`
    font-size: ${theme.typography.inherit.fontSize};
    line-height: ${theme.typography.inherit.lineHeight};
  `}

  :hover {
    text-decoration: none;
    background-color: rgba(0, 0, 0, 0.04);
  }
`

interface ListItemProps<AsButton extends boolean = false> extends Omit<React.HTMLAttributes<HTMLLIElement>, 'onClick'> {
  button?: boolean
  dense?: boolean
  disabled?: boolean
  disableGutters?: boolean
  divider?: boolean
  menuItem?: boolean
  onClick?: AsButton extends true ? React.MouseEventHandler<HTMLButtonElement> : React.MouseEventHandler<HTMLLIElement>
  selected?: boolean
}

export function ListItem<AsButton extends boolean = false>({
  button = false,
  dense = false,
  disabled = false,
  disableGutters = false,
  divider = false,
  children,
  menuItem = false,
  onClick,
  selected = false,
  ...rest
}: ListItemProps<AsButton>) {
  return (
    <StyledListItem
      $dense={dense}
      $disableGutters={disableGutters}
      $divider={divider}
      {...rest}
      style={{ ...rest.style, ...getCssVariables(dense, disableGutters) }}
      // onClick is expected to be a MouseEventHandler<HTMLLIElement> when button is false
      {...(!button && {
        onClick: onClick as React.MouseEventHandler<HTMLLIElement>,
      })}
      {...(menuItem && {
        role: 'menuitem',
        tabIndex: -1,
        'aria-disabled': disabled,
        'data-selected': selected,
      })}
    >
      {button ? (
        // onClick is expected to be a MouseEventHandler<HTMLButtonElement> when button is true
        <ListItemButton onClick={onClick as unknown as React.MouseEventHandler<HTMLButtonElement>}>
          {children}
        </ListItemButton>
      ) : (
        <>{children}</>
      )}
    </StyledListItem>
  )
}

const StyledListItemText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-top: 4px;
  margin-bottom: 4px;

  & > * {
    display: block;
  }

  &:has(> p) {
    margin-top: 6px;
    margin-bottom: 6px;
  }

  > p {
    color: rgba(0, 0, 0, 0.54);
  }
`

const PrimaryTypography = styled(Typography)`
  // Allow prop driven overrides to avoid specificity issues with external stylings
  text-align: ${({ align }) => align || 'var(--primary-text-align, left)'};
`

interface ListItemTextProps extends React.HTMLAttributes<HTMLDivElement> {
  primary?: React.ReactNode
  secondary?: React.ReactNode
  primaryTypographyProps?: Omit<TypographyProps, 'children'>
  secondaryTypographyProps?: Omit<TypographyProps, 'children'>
}

export function ListItemText({
  children,
  primary,
  primaryTypographyProps,
  secondary,
  secondaryTypographyProps,
  ...rest
}: ListItemTextProps) {
  return (
    <StyledListItemText {...rest}>
      <PrimaryTypography tag="span" size="400-lg" {...primaryTypographyProps}>
        {primary || children}
      </PrimaryTypography>
      {secondary && (
        <Typography {...secondaryTypographyProps} align={secondaryTypographyProps?.align || 'center'}>
          {secondary}
        </Typography>
      )}
    </StyledListItemText>
  )
}

export const ListItemIcon = styled.div<{ $minWidth?: string }>`
  color: rgba(0, 0, 0, 0.54);
  display: inline-flex;
  min-width: ${({ $minWidth }) => $minWidth || '56px'};
  flex-shrink: 0;
`
